// ========================================
// MAIN APP COMPONENT - ASK OPERATIONS DASHBOARD
// ========================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import SystemHealth from './components/SystemHealth';
import config from './config';
import TeamManagement from './components/TeamManagement';
import Settings from './components/Settings';
import AuditLog from './components/AuditLog';
import ChatSidebar from './components/ChatSidebar';
import TeamDetailsModal from './components/TeamDetailsModal';
import UserDetailsModal from './components/UserDetailsModal';
import Sidebar from './components/Sidebar';
import {
    getSystemHealth,
    getOpenWebUIHealth,
    getOpenWebUIActiveUsers,
    getOpenWebUIModels,
    getOpenWebUIGroups,
    getLiteLLMTeams,
    blockTeam,
    pingLiteLLM,
    pingOpenWebUI,
    getDepartments,
    getMyDepartment,
    getMyTeam,
    triggerDepartmentSync,
    getAdminUsers,
    getDisabledUsersCount,
    getUnassignedUsersCount,
    getUnassignedUsersList,
    getRecentlyProvisioned,
    getMCPServers,
    getOpenWebUITools,
    getOpenWebUISkills,
    getModelHealthCache,
    triggerModelHealthCheck,
    getUserPreferences,
    saveUserPreferences,
} from './services/api';
import { authenticatedFetch, getIdToken, getAccessToken } from './services/authService';
import { shouldBlockTeam, getTeamThresholds, cleanupOrphanedThresholds, getGlobalThresholds } from './utils/thresholds';
import {
    reportPing,
    checkAndSendBudgetAlerts,
    sendTeamBlockedAlert,
    getWebhookConfig
} from './services/webhookService';
import { canAccessSystemSettings, setAdminList } from './utils/permissions';
import {
    connectSocket,
    disconnectSocket,
    onSocketEvent,
    setSocketToken,
    SOCKET_EVENTS
} from './services/socketService';

// Custom hooks
import useAutoRefresh from './hooks/useAutoRefresh';
import useLoadingMessages from './hooks/useLoadingMessages';
import useRefreshCooldown from './hooks/useRefreshCooldown';
import useCacheStorage from './hooks/useCacheStorage';

// Constants
import { CACHE_KEYS, REFRESH_INTERVALS } from './constants/dashboard';
import UsageCostDashboard from './components/UsageCostDashboard';
import ProvisionModal from './components/ProvisionModal';

// ========================================
// AUTO-REFRESH INTERVALS
// ========================================
const AUTO_REFRESH_INTERVALS = {
    ACTIVE_USERS: 60000,      // 60 seconds
    PING_DISPLAY: 60000,      // 60 seconds — UI-only ping refresh (no alert logic)
    HEALTH_CHECK: 900000,     // 15 minutes — full health check with alert reporting
    BUDGET_REFRESH: 900000,   // 15 minutes
    BUDGET_MONITOR: 900000    // 15 minutes
};

// Default visible team aliases for new users (support staff view)

function App() {
    const { instance, accounts } = useMsal();
    const { t, i18n } = useTranslation();
    // API keys are now stored on the backend only - always considered configured if backend URL is set
    const isConfigured = config.backendApiUrl && config.backendApiUrl.trim().length > 0;

    // Current user — needed early for preferences
    const userEmail = accounts[0]?.username || accounts[0]?.email || 'Unknown User';
    const isCurrentUserAdmin = canAccessSystemSettings(userEmail);

    // Theme — localStorage for instant render, backend prefs for cross-device persistence
    const [theme, setTheme] = useState(() => localStorage.getItem('ask-operations-theme') || 'dark');
    const themeLoadedRef = useRef(false);

    // On first authenticated render, load the saved theme from the backend
    useEffect(() => {
        if (themeLoadedRef.current || !userEmail || userEmail === 'Unknown User') return;
        themeLoadedRef.current = true;
        getUserPreferences(userEmail).then(result => {
            if (result.success && result.data?.theme) {
                setTheme(result.data.theme);
            }
        }).catch(() => {});
    }, [userEmail]);

    useEffect(() => {
        document.body.setAttribute('data-theme', theme);
        localStorage.setItem('ask-operations-theme', theme);
    }, [theme]);

    const toggleTheme = () => {
        setTheme(t => {
            const next = t === 'dark' ? 'light' : 'dark';
            // Persist to backend so preference follows the user across devices
            if (userEmail && userEmail !== 'Unknown User') {
                saveUserPreferences(userEmail, { theme: next }).catch(() => {});
            }
            return next;
        });
    };

    // UI State
    const [lastRefresh, setLastRefresh] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [showProvisionModal, setShowProvisionModal] = useState(false);
    const [showAuditLog, setShowAuditLog] = useState(false);
    const [showChatSidebar, setShowChatSidebar] = useState(false);
    const [showAdmin, setShowAdmin] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentView, setCurrentView] = useState('dashboard');
    const [userOrgUnit, setUserOrgUnit] = useState(null);
    const [userDepartment, setUserDepartment] = useState(null);
    const [userPhoto, setUserPhoto] = useState(null);
    const [isManager, setIsManager] = useState(false);
    const [myDirectReports, setMyDirectReports] = useState([]); // 'dashboard' | 'analytics' | 'settings' | 'reports'
    const [settingsSubTab, setSettingsSubTab] = useState('thresholds');
    const [analyticsSubTab, setAnalyticsSubTab] = useState('my-usage');
    const [reportInitialType, setReportInitialType] = useState('');

    // Close Admin dropdown when clicking outside
    // Chat-triggered modal state (for opening modals from AI chat)
    const [chatSelectedTeam, setChatSelectedTeam] = useState(null);
    const [chatSelectedUser, setChatSelectedUser] = useState(null);

    // Notifications bell state
    const [notifications, setNotifications] = useState([]);
    const [notificationsLoaded, setNotificationsLoaded] = useState(false);
    const [hasUnread, setHasUnread] = useState(false);
    const [notificationsOpen, setNotificationsOpen] = useState(false);

    // Lock body scroll whenever any overlay/panel is open
    useEffect(() => {
        const anyOpen = sidebarOpen || showAuditLog || showChatSidebar || notificationsOpen || showProvisionModal;
        document.body.style.overflow = anyOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [sidebarOpen, showAuditLog, showChatSidebar, notificationsOpen, showProvisionModal]);

    // AUTO-REFRESH STATE
    // Only enable auto-refresh when user is authenticated (has accounts)
    const autoRefreshEnabled = accounts && accounts.length > 0;
    const [lastActiveUsersCheck, setLastActiveUsersCheck] = useState(null);


    // Freshness check timer
    const freshnessTimerRef = useRef(null);

    // Loading status state
    const [loadingStatus, setLoadingStatus] = useState({
        health: 'idle',
        teams: 'idle',
        models: 'idle',
        openWebUI: 'idle',
        users: 'idle',
        groups: 'idle',
        overall: 'idle'
    });

    // Data State
    const [dashboardData, setDashboardData] = useState({
        health: null,
        teams: [],
        models: [],
        openWebUIHealth: null,
        activeUsers: 0,
        activeUserNames: [],
        users: [],
        groups: [],
        departments: null,
        adminUsers: null,
        disabledUsersCount: null,
        unassignedUsersCount: null,
        unassignedUsersList: [],
        recentlyProvisioned: [],
        loading: true,
        error: null,
        // URL ping status for connectivity checks
        litellmPing: null,  // { reachable, status, latency, error }
        openWebUIPing: null // { reachable, status, latency, error }
    });

    const [renderKey, setRenderKey] = useState(0);


    // ========================================
    // BROWSER TAB TITLE
    // ========================================

    useEffect(() => {
        document.title = 'Ask Operations';
    }, []);

    // ========================================
    // CUSTOM HOOKS
    // ========================================

    // Refresh cooldown hook
    const {
        isOnCooldown,
        remainingSeconds,
        startCooldown
    } = useRefreshCooldown(60);

    // Cache storage hook
    const {
        cacheInfo,
        setCacheInfo,
        loadFromLocalStorage,
        saveToLocalStorage,
        formatCacheAge,
        isStale,
        getFreshnessStatus
    } = useCacheStorage({
        cacheKey: CACHE_KEYS.DASHBOARD_CACHE,
        lastFetchKey: CACHE_KEYS.DASHBOARD_LAST_FETCH,
        staleThreshold: REFRESH_INTERVALS.STALE_THRESHOLD
    });

    // Loading messages hooks - get translated arrays
    const healthLoadingMessages = t('loading.health', { returnObjects: true });
    const teamLoadingMessages = t('loading.teams', { returnObjects: true });

    const healthLoadingMessage = useLoadingMessages(
        healthLoadingMessages,
        loadingStatus.overall === 'loading' || loadingStatus.health === 'loading',
        3000
    );

    const teamLoadingMessage = useLoadingMessages(
        teamLoadingMessages,
        loadingStatus.overall === 'loading' || loadingStatus.teams === 'loading',
        3000
    );

    const freshnessStatus = getFreshnessStatus();

    // ========================================
    // HELPER FUNCTIONS
    // ========================================

    /**
     * Format a timestamp as human-readable string showing time elapsed
     */
    const formatTimeSince = (timestamp) => {
        if (!timestamp) return '';
        const now = new Date();
        const seconds = Math.floor((now - timestamp) / 1000);
        if (seconds < 60) return `${seconds}s ago`;
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        return `${hours}h ago`;
    };

    /**
     * Get text for refresh button based on current state
     */
    const getRefreshButtonText = () => {
        if (isRefreshing) return t('common.refreshing');
        if (isOnCooldown) return t('common.waitSeconds', { seconds: remainingSeconds });
        return t('common.refreshNow');
    };

    // ========================================
    // COMPONENT LIFECYCLE
    // ========================================

    /**
     * Cleanup orphaned team thresholds when teams data changes
     */
    useEffect(() => {
        if (dashboardData.teams && dashboardData.teams.length > 0) {
            // Cleanup orphaned team thresholds (teams that no longer exist)
            const currentTeamIds = dashboardData.teams.map(t => t.team_id);
            cleanupOrphanedThresholds(currentTeamIds);
        }
    }, [dashboardData.teams]);

    /**
     * Handle user logout
     */
    const handleLogout = () => {
        instance.logoutRedirect({
            postLogoutRedirectUri: '/',
        });
    };

    // ========================================
    // DATA FETCHING FUNCTIONS
    // ========================================

    // Ref to track if active users refresh is in progress (prevents overlapping calls)
    const activeUsersRefreshInProgress = useRef(false);

    /**
     * Tier 0: Ultra-lightweight active users refresh (every 60 seconds)
     */
    const refreshActiveUsersOnly = useCallback(async () => {
        if (!autoRefreshEnabled) return;

        // Prevent overlapping calls
        if (activeUsersRefreshInProgress.current) {
            return;
        }

        activeUsersRefreshInProgress.current = true;

        try {
            const activeUsersResult = await getOpenWebUIActiveUsers();

            if (activeUsersResult.success) {
                setDashboardData(prev => ({
                    ...prev,
                    activeUsers: activeUsersResult.data.count,
                    activeUserNames: activeUsersResult.data.user_names || []
                }));
                setLastActiveUsersCheck(new Date());
            }
        } catch (error) {
            // Silent failure - active users is non-critical
        } finally {
            activeUsersRefreshInProgress.current = false;
        }
    }, [autoRefreshEnabled]);

    // Ref to track if health check is in progress
    const healthCheckInProgress = useRef(false);

    // Consecutive failure threshold — shared across all browser tabs via the backend.
    // The backend /report-ping endpoint holds authoritative counters in webhook-config.json
    // so page reloads and multiple open tabs all share the same state.
    const PING_THRESHOLD = 2;

    /**
     * Tier 1: Lightweight health check (every 15 minutes)
     * Pings both services and reports results to the backend, which manages
     * consecutive-failure counters and fires alerts atomically.
     */
    const lightweightHealthCheck = useCallback(async () => {
        if (!autoRefreshEnabled) return;

        // Prevent overlapping calls
        if (healthCheckInProgress.current) {
            return;
        }

        healthCheckInProgress.current = true;

        try {
            // Run ping checks in parallel — no /health call here to avoid model-ping log spam
            const [openWebUIHealthResult, litellmPingResult, openWebUIPingResult] = await Promise.all([
                getOpenWebUIHealth(),
                pingLiteLLM(),
                pingOpenWebUI()
            ]);

            // Update dashboard data with all results
            setDashboardData(prev => ({
                ...prev,
                openWebUIHealth: openWebUIHealthResult.success ? 'healthy' : 'unhealthy',
                litellmPing: litellmPingResult,
                openWebUIPing: openWebUIPingResult
            }));

            // Report ping results to the backend. The backend holds the authoritative
            // consecutive-failure counters in webhook-config.json (under failCounts) and
            // fires alerts atomically under a file lock. This means all browser tabs and
            // page reloads share the same counters — no tab can fire independently.
            await reportPing({
                litellmReachable:    litellmPingResult?.reachable === true,
                openwebuiReachable:  openWebUIPingResult?.reachable === true,
                litellmLatency:      litellmPingResult?.latency || 0,
                openwebuiLatency:    openWebUIPingResult?.latency || 0,
                threshold:           PING_THRESHOLD,
            });
        } catch (error) {
            // Silent failure - health check runs in background
        } finally {
            healthCheckInProgress.current = false;
        }
    }, [autoRefreshEnabled]);

    // Ref to prevent overlapping ping-display refreshes
    const pingDisplayInProgress = useRef(false);

    /**
     * Tier 0.5: Ping display refresh (every 60 seconds)
     * Updates latency + online/offline status on the System Health cards without
     * touching the alert-reporting logic (reportPing stays in lightweightHealthCheck
     * at 15-minute cadence to avoid alert spam).
     */
    const refreshPingDisplay = useCallback(async () => {
        if (!autoRefreshEnabled) return;

        if (pingDisplayInProgress.current) return;
        pingDisplayInProgress.current = true;

        try {
            const [litellmPingResult, openWebUIPingResult] = await Promise.all([
                pingLiteLLM(),
                pingOpenWebUI()
            ]);

            setDashboardData(prev => ({
                ...prev,
                litellmPing: litellmPingResult,
                openWebUIPing: openWebUIPingResult
            }));
        } catch {
            // Silent failure
        } finally {
            pingDisplayInProgress.current = false;
        }
    }, [autoRefreshEnabled]);

    // Ref to track if budget monitor is in progress
    const budgetMonitorInProgress = useRef(false);
    // Ref to hold current teams data without triggering re-renders
    const teamsRef = useRef(dashboardData.teams);

    // Keep teams ref updated
    useEffect(() => {
        teamsRef.current = dashboardData.teams;
    }, [dashboardData.teams]);

    /**
     * Monitor team budgets and auto-block teams that exceed their limits
     */
    const monitorAndEnforceBudgets = useCallback(async () => {
        const teams = teamsRef.current;

        if (!autoRefreshEnabled || !teams || teams.length === 0) {
            return;
        }

        // Prevent overlapping calls
        if (budgetMonitorInProgress.current) {
            return;
        }

        budgetMonitorInProgress.current = true;

        try {
            const teamsToBlock = [];

            for (const team of teams) {
                const maxBudget = team.max_budget || 0;
                const isBlocked = team.blocked || false;

                // Use configurable threshold (per-team or global default)
                if (maxBudget > 0 && !isBlocked && shouldBlockTeam(team)) {
                    const thresholds = getTeamThresholds(team.team_id);
                    teamsToBlock.push({ team, blockThreshold: thresholds.budgetBlock });
                }
            }

            if (teamsToBlock.length > 0) {
                for (const { team, blockThreshold } of teamsToBlock) {
                    const thresholdPercent = Math.round(blockThreshold * 100);

                    await blockTeam(
                        team.team_id,
                        'System',
                        team.team_alias,
                        `Automatic block: Budget threshold (${thresholdPercent}%) exceeded ($${team.spend.toFixed(2)} / $${team.max_budget.toFixed(2)})`
                    );

                    // Send webhook alert for team blocked
                    await sendTeamBlockedAlert(
                        team.team_alias,
                        team.team_id,
                        `Budget threshold (${thresholdPercent}%) exceeded`
                    );
                }
            }

            // Check and send budget warning alerts — exclude teams we just auto-blocked
            // (those already got a teamBlocked alert above; sending budgetExceeded too causes duplicates)
            const justBlockedIds = new Set(teamsToBlock.map(({ team }) => team.team_id));
            const teamsForBudgetAlerts = teams.filter(t => !justBlockedIds.has(t.team_id));
            const globalThresholds = getGlobalThresholds();
            await checkAndSendBudgetAlerts(teamsForBudgetAlerts, {
                alert: globalThresholds.budgetAlert * 100,
                block: globalThresholds.budgetBlock * 100
            });
        } finally {
            budgetMonitorInProgress.current = false;
        }
    }, [autoRefreshEnabled]);

    /**
     * Fetch all users with pagination
     * Uses authenticatedFetch to include Azure AD token for backend proxy
     */
    const fetchAllUsers = async () => {
        setLoadingStatus(prev => ({ ...prev, users: 'loading' }));

        try {
            const firstPageUrl = `${config.openWebUIBaseUrl}/api/v1/users/?page=1&order_by=created_at&direction=asc`;
            const firstResponse = await authenticatedFetch(firstPageUrl, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!firstResponse.ok) {
                setLoadingStatus(prev => ({ ...prev, users: 'error' }));
                return [];
            }

            const firstData = await firstResponse.json();
            const totalUsers = firstData.total || 0;
            const usersPerPage = 30;
            const totalPages = Math.ceil(totalUsers / usersPerPage);

            if (totalPages <= 1) {
                const filteredUsers = (firstData.users || []).filter(user =>
                    user.email && user.email.endsWith('@aware.co.th')
                );
                setLoadingStatus(prev => ({ ...prev, users: 'complete' }));
                return filteredUsers;
            }

            // Fetch remaining pages in parallel using authenticatedFetch
            const pagePromises = [];
            for (let page = 2; page <= totalPages; page++) {
                const url = `${config.openWebUIBaseUrl}/api/v1/users/?page=${page}&order_by=created_at&direction=asc`;
                pagePromises.push(
                    authenticatedFetch(url, {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    }).then(res => res.ok ? res.json() : null)
                );
            }

            const remainingPages = await Promise.all(pagePromises);

            const allUsers = [
                ...(firstData.users || []),
                ...remainingPages.filter(data => data).flatMap(pageData => pageData.users || [])
            ];

            const filteredUsers = allUsers.filter(user =>
                user.email && user.email.endsWith('@aware.co.th')
            );

            setLoadingStatus(prev => ({ ...prev, users: 'complete' }));
            return filteredUsers;

        } catch (error) {
            setLoadingStatus(prev => ({ ...prev, users: 'error' }));
            return [];
        }
    };

    /**
     * Fetch teams from LiteLLM and groups from OpenWebUI
     */
    const fetchTeamsOnly = useCallback(async () => {
        try {
            const [groupsResult, teamsResult, deptResult] = await Promise.all([
                getOpenWebUIGroups(),
                getLiteLLMTeams(),
                getDepartments().catch(() => ({ success: false }))
            ]);

            let groups = [];
            let teams = [];

            if (groupsResult.success) {
                groups = Array.isArray(groupsResult.data) ? groupsResult.data : [];
            }

            if (teamsResult.success) {
                const litellmTeams = Array.isArray(teamsResult.data) ? teamsResult.data : [];
                teams = litellmTeams.map(team => {
                    // Calculate total cumulative spend from all keys in the team
                    // Note: team.spend is the budget-period spend (resets based on budget_duration),
                    // while key.spend is cumulative total spend
                    const totalKeySpend = (team.keys || []).reduce((sum, key) => {
                        return sum + (key.spend || 0);
                    }, 0);

                    return {
                        ...team,
                        team_id: team.team_id,
                        team_alias: team.team_alias || team.team_id,
                        max_budget: team.max_budget || 0,
                        spend: team.spend || 0, // Budget period spend (for comparison with max_budget)
                        total_spend: totalKeySpend, // Cumulative total spend
                        blocked: team.blocked || false,
                        models: team.models || [],
                        budget_duration: team.budget_duration || null
                    };
                });
            } else if (groups.length > 0) {
                // Fallback to groups if LiteLLM failed
                teams = groups.map(group => ({
                    team_id: group.id,
                    team_alias: group.name,
                    max_budget: 0,
                    spend: 0,
                    blocked: false,
                    models: []
                }));
            }

            setDashboardData(prev => ({
                ...prev,
                teams: teams,
                groups: groups,
                ...(deptResult.success ? { departments: deptResult.data } : {})
            }));

            setRenderKey(prev => prev + 1);
        } catch (error) {
            // Silent failure
        }
    }, []);

    /**
     * Progressive data fetching with localStorage caching
     * @param {boolean} forceHealthRefresh - Force fresh health check bypassing cache
     */
    const fetchAllData = useCallback(async (forceHealthRefresh = false) => {
        setIsRefreshing(true);

        setLoadingStatus({
            health: 'loading',
            teams: 'loading',
            models: 'loading',
            openWebUI: 'loading',
            users: 'loading',
            groups: 'loading',
            overall: 'loading'
        });

        setDashboardData(prev => ({ ...prev, loading: true, error: null }));

        try {
            // STEP 1: Check if this is a new session (force fresh fetch on login)
            const isNewSession = !sessionStorage.getItem(CACHE_KEYS.SESSION_INITIALIZED);
            if (isNewSession) {
                sessionStorage.setItem(CACHE_KEYS.SESSION_INITIALIZED, 'true');
            }

            // STEP 2: Check localStorage cache (skip if new session)
            try {
                const localResult = loadFromLocalStorage(1);

                if (localResult && localResult.snapshot && !isNewSession) {
                    // Always fetch fresh active users count (real-time data)
                    let freshActiveUsers = localResult.snapshot.activeUsers || 0;
                    let freshActiveUserNames = localResult.snapshot.activeUserNames || [];
                    try {
                        const activeUsersResult = await getOpenWebUIActiveUsers();
                        if (activeUsersResult.success) {
                            freshActiveUsers = activeUsersResult.data.count;
                            freshActiveUserNames = activeUsersResult.data.user_names || [];
                        }
                    } catch (e) {
                        // Use cached value on error
                    }

                    setDashboardData({
                        ...localResult.snapshot,
                        activeUsers: freshActiveUsers,
                        activeUserNames: freshActiveUserNames,
                        departments: null,
                        loading: false
                    });

                    // Departments and admin list are never stored in localStorage — always fetch fresh
                    getDepartments().then(r => {
                        if (r.success) {
                            setDashboardData(prev => ({ ...prev, departments: r.data }));
                            setRenderKey(prev => prev + 1);
                        }
                    }).catch(() => {});
                    getAdminUsers().then(r => {
                        if (r.success) {
                            setDashboardData(prev => ({ ...prev, adminUsers: r }));
                            setAdminList(r.admins || []);
                        }
                    }).catch(() => {});
                    getDisabledUsersCount().then(count => {
                        if (count !== null) setDashboardData(prev => ({ ...prev, disabledUsersCount: count }));
                    }).catch(() => {});
                    if (isCurrentUserAdmin) {
                        getUnassignedUsersCount().then(count => {
                            if (count !== null) setDashboardData(prev => ({ ...prev, unassignedUsersCount: count }));
                        }).catch(() => {});
                        getUnassignedUsersList().then(entries => {
                            if (entries !== null) setDashboardData(prev => ({ ...prev, unassignedUsersList: entries }));
                        }).catch(() => {});
                        getRecentlyProvisioned().then(entries => {
                            if (entries !== null) setDashboardData(prev => ({ ...prev, recentlyProvisioned: entries }));
                        }).catch(() => {});
                    }

                    setCacheInfo({
                        source: 'localStorage',
                        age: localResult.age,
                        savedAt: localResult.savedAt
                    });

                    setIsRefreshing(false);
                    setLoadingStatus(prev => ({ ...prev, overall: 'complete' }));
                    setLastActiveUsersCheck(new Date());

                    // Check if cache is fresh enough AND has complete data
                    // Note: Users are NOT cached (fetched fresh each session to save storage)
                    const hasGroups = localResult.snapshot.groups && localResult.snapshot.groups.length > 0;
                    const hasTeams = localResult.snapshot.teams && localResult.snapshot.teams.length > 0;

                    if (!isStale(localResult.age) && hasGroups && hasTeams) {
                        // Mark most loading statuses as complete, but users still need to load
                        setLoadingStatus({
                            overall: 'complete',
                            health: 'complete',
                            openWebUI: 'complete',
                            models: 'complete',
                            teams: 'complete',
                            groups: 'complete',
                            users: 'loading' // Users always fetched fresh
                        });

                        // Fetch users in background even when using cached data
                        fetchAllUsers().then(allUsers => {
                            setDashboardData(prev => ({
                                ...prev,
                                users: allUsers
                            }));
                            setLoadingStatus(prev => ({ ...prev, users: 'complete' }));
                            setRenderKey(prev => prev + 1);
                        }).catch(() => {
                            setLoadingStatus(prev => ({ ...prev, users: 'error' }));
                        });

                        return;
                    }

                    setIsRefreshing(true);
                }
            } catch (cacheError) {
                // Continue with fresh fetch
            }

            // STEP 2: Fetch fresh data from APIs
            const newData = {
                health: null,
                teams: [],
                models: [],
                openWebUIHealth: null,
                activeUsers: 0,
                users: [],
                groups: [],
                loading: false,
                error: null,
                litellmPing: null,
                openWebUIPing: null
            };

            // Fire health check, department sync, and pings in the background — they must NOT block team/model/group data.
            // LiteLLM's /health endpoint tests every model providers and can take 30–120s,
            // causing a 504 that previously blocked the entire Promise.all() and delayed all rendering.
            // When user clicks "Refresh Now", we force a fresh health check to bypass cache
            getDepartments().then(async result => {
                if (result.success) {
                    setDashboardData(prev => ({ ...prev, departments: result.data }));
                    setRenderKey(prev => prev + 1);
                    // Auto-sync if cache is empty or older than 365 days — any authenticated user can trigger this
                    const isEmpty = !result.data?.totalUsers || result.data.totalUsers === 0;
                    const lastSync = result.data?.lastFullSync ? new Date(result.data.lastFullSync) : null;
                    const isStale = !lastSync || (Date.now() - lastSync.getTime() > 365 * 24 * 60 * 60 * 1000);
                    if (isEmpty || isStale) {
                        try {
                            const account = accounts[0];
                            let graphToken;
                            try {
                                const tokenRes = await instance.acquireTokenSilent({
                                    scopes: ['https://graph.microsoft.com/User.Read.All'],
                                    account
                                });
                                graphToken = tokenRes.accessToken;
                            } catch {
                                const tokenRes = await instance.acquireTokenPopup({
                                    scopes: ['https://graph.microsoft.com/User.Read.All'],
                                    account
                                });
                                graphToken = tokenRes.accessToken;
                            }
                            const syncResult = await triggerDepartmentSync(graphToken);
                            if (syncResult.success) {
                                const refreshed = await getDepartments();
                                if (refreshed.success) {
                                    setDashboardData(prev => ({ ...prev, departments: refreshed.data }));
                                    setRenderKey(prev => prev + 1);
                                }
                            }
                        } catch { /* silent — user can manually sync */ }
                    }
                }
                // Fetch current user's org/dept for sidebar display
                getMyDepartment().then(d => {
                    if (d.orgUnit) setUserOrgUnit(d.orgUnit);
                    if (d.department) setUserDepartment(d.department);
                }).catch(() => {});
                // Fetch direct reports to determine manager status
                getMyTeam().then(t => {
                    setIsManager(t.isManager);
                    setMyDirectReports(t.directReports);
                }).catch(() => {});
            }).catch(() => {});
            // Fetch Teams profile photo for sidebar avatar — runs unconditionally after auth
            getAccessToken().then(token => {
                if (!token) throw new Error('no token');
                return fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }).then(res => {
                if (res.ok) return res.blob();
                throw new Error('no photo');
            }).then(blob => {
                setUserPhoto(URL.createObjectURL(blob));
            }).catch(() => {});
            getAdminUsers().then(r => {
                if (r.success) {
                    setDashboardData(prev => ({ ...prev, adminUsers: r }));
                    setAdminList(r.admins || []);
                }
            }).catch(() => {});
            getDisabledUsersCount().then(count => {
                if (count !== null) setDashboardData(prev => ({ ...prev, disabledUsersCount: count }));
            }).catch(() => {});
            if (isCurrentUserAdmin) {
                getUnassignedUsersCount().then(count => {
                    if (count !== null) setDashboardData(prev => ({ ...prev, unassignedUsersCount: count }));
                }).catch(() => {});
                getUnassignedUsersList().then(entries => {
                    if (entries !== null) setDashboardData(prev => ({ ...prev, unassignedUsersList: entries }));
                }).catch(() => {});
                getRecentlyProvisioned().then(entries => {
                    if (entries !== null) setDashboardData(prev => ({ ...prev, recentlyProvisioned: entries }));
                }).catch(() => {});
            }

            pingLiteLLM().then(result => {
                setDashboardData(prev => ({ ...prev, litellmPing: result }));
            }).catch(() => {});

            pingOpenWebUI().then(result => {
                setDashboardData(prev => ({ ...prev, openWebUIPing: result }));
            }).catch(() => {});

            const [
                openWebUIHealthResult,
                modelResult,
                activeUsersResult,
                groupsResult,
                litellmTeamsResult,
                mcpServersResult,
                owuiToolsResult,
                owuiSkillsResult,
                modelHealthCacheResult,
            ] = await Promise.all([
                getOpenWebUIHealth().then(result => {
                    setLoadingStatus(prev => ({ ...prev, openWebUI: result.success ? 'complete' : 'error' }));
                    return result;
                }),
                getOpenWebUIModels().then(result => {
                    setLoadingStatus(prev => ({ ...prev, models: result.success ? 'complete' : 'error' }));
                    return result;
                }),
                getOpenWebUIActiveUsers().catch(() => {
                    return { success: false, data: { count: 0 } };
                }),
                getOpenWebUIGroups().then(result => {
                    setLoadingStatus(prev => ({ ...prev, groups: result.success ? 'complete' : 'error' }));
                    return result;
                }),
                getLiteLLMTeams().then(result => {
                    setLoadingStatus(prev => ({ ...prev, teams: result.success ? 'complete' : 'error' }));
                    return result;
                }),
                getMCPServers().catch(() => ({ success: false, data: [] })),
                getOpenWebUITools().catch(() => ({ success: false, data: [] })),
                getOpenWebUISkills().catch(() => ({ success: false, data: [] })),
                getModelHealthCache().catch(() => ({ success: false, cache: null })),
            ]);

            // Load OpenWebUI groups (for user membership)
            if (groupsResult.success) {
                const groups = Array.isArray(groupsResult.data) ? groupsResult.data : [];
                newData.groups = groups;
            }

            // Load LiteLLM teams (for budget/model data) and merge with groups
            if (litellmTeamsResult.success) {
                const litellmTeams = Array.isArray(litellmTeamsResult.data) ? litellmTeamsResult.data : [];

                // Use LiteLLM teams as the primary source (they have budget/model data)
                // The team_alias in LiteLLM should match group.name in OpenWebUI
                newData.teams = litellmTeams.map(team => {
                    // Calculate total cumulative spend from all keys in the team
                    // Note: team.spend is the budget-period spend (resets based on budget_duration),
                    // while key.spend is cumulative total spend
                    const totalKeySpend = (team.keys || []).reduce((sum, key) => {
                        return sum + (key.spend || 0);
                    }, 0);

                    return {
                        ...team,
                        // Ensure these fields exist
                        team_id: team.team_id,
                        team_alias: team.team_alias || team.team_id,
                        max_budget: team.max_budget || 0,
                        spend: team.spend || 0, // Budget period spend (for comparison with max_budget)
                        total_spend: totalKeySpend, // Cumulative total spend
                        blocked: team.blocked || false,
                        models: team.models || [],
                        budget_duration: team.budget_duration || null
                    };
                });
            } else {
                // Fallback: create teams from groups if LiteLLM fetch failed
                if (newData.groups.length > 0) {
                    newData.teams = newData.groups.map(group => ({
                        team_id: group.id,
                        team_alias: group.name,
                        max_budget: 0,
                        spend: 0,
                        blocked: false,
                        models: []
                    }));
                }
            }

            // Process OpenWebUI health
            const isOpenWebUIHealthy = openWebUIHealthResult.success;
            newData.openWebUIHealth = isOpenWebUIHealthy ? 'healthy' : 'unhealthy';

            // Process models data
            if (modelResult.success) {
                const allModels = modelResult.data?.data || [];
                newData.models = allModels.filter(model => {
                    const modelName = model.id || '';
                    return modelName.startsWith('PLUS.') ||
                        modelName.startsWith('CNX.') ||
                        modelName.startsWith('BKK.');
                });
            }

            // Process active users
            if (activeUsersResult.success) {
                newData.activeUsers = activeUsersResult.data.count;
                newData.activeUserNames = activeUsersResult.data.user_names || [];
                setLastActiveUsersCheck(new Date());
            } else {
                newData.activeUsers = 0;
                newData.activeUserNames = [];
            }

            // Process MCP servers
            newData.mcpServers = mcpServersResult.success ? (mcpServersResult.data || []) : [];

            // Process OpenWebUI tools (enabled only)
            newData.owuiTools = owuiToolsResult.success ? (owuiToolsResult.data || []) : [];

            // Process OpenWebUI skills (enabled only)
            newData.owuiSkills = owuiSkillsResult.success ? (owuiSkillsResult.data || []) : [];

            // Restore persisted model health (survives pod restarts)
            if (modelHealthCacheResult.success && modelHealthCacheResult.cache) {
                const cached = modelHealthCacheResult.cache;
                newData.health = {
                    healthy_endpoints: cached.healthy_endpoints || [],
                    unhealthy_endpoints: cached.unhealthy_endpoints || [],
                };
                newData.modelHealthCheckedAt = cached.checkedAt || null;
            }

            // Render dashboard with critical data immediately
            setDashboardData({
                ...newData,
                loading: false
            });
            setRenderKey(prev => prev + 1);

            const now = new Date();
            setLastRefresh(now);

            setIsRefreshing(false);
            setLoadingStatus(prev => ({ ...prev, overall: 'complete' }));

            // Save to localStorage (excluding users to keep cache small)
            const cacheData = { ...newData };
            delete cacheData.users; // Users are fetched fresh each time - don't cache
            saveToLocalStorage(cacheData, 1);

            // Update cache info
            setCacheInfo({
                source: 'fresh_fetch',
                age: 0,
                savedAt: now.toISOString()
            });

            // Phase 2: Load users in background
            fetchAllUsers().then(allUsers => {
                setDashboardData(prev => ({
                    ...prev,
                    users: allUsers
                }));
                setRenderKey(prev => prev + 1);
                // Note: Users are NOT cached to localStorage to keep storage small
                // They are fetched fresh each session
            }).catch(() => {
                setLoadingStatus(prev => ({ ...prev, users: 'error' }));
            });

        } catch (error) {
            setDashboardData(prev => ({
                ...prev,
                loading: false,
                error: error.message
            }));
            setIsRefreshing(false);
            setLoadingStatus(prev => ({ ...prev, overall: 'error' }));
        }
    }, [loadFromLocalStorage, saveToLocalStorage, isStale, setCacheInfo]);

    // ── Notifications bell — load recent alert actions from audit log ──────────
    useEffect(() => {
        if (!autoRefreshEnabled || notificationsLoaded) return;
        setNotificationsLoaded(true);
        const ALERT_ACTIONS = ['team_blocked', 'user_blocked', 'team_budget_updated', 'team_unblocked'];
        authenticatedFetch('/api/audit?limit=50')
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (!data) return;
                const entries = Array.isArray(data) ? data : (data.logs || data.entries || []);
                const alerts = entries
                    .filter(e => ALERT_ACTIONS.includes(e.action))
                    .slice(0, 20)
                    .map(e => ({
                        id: e.timestamp || e.server_timestamp || Math.random(),
                        action: e.action,
                        details: e.details || '',
                        email: e.email || '',
                        timestamp: e.timestamp || e.server_timestamp || '',
                    }));
                setNotifications(alerts);
                if (alerts.length > 0) setHasUnread(true);
            })
            .catch(() => {});
    }, [autoRefreshEnabled, notificationsLoaded]);

    // ========================================
    // SMART AUTO-REFRESH
    // ========================================

    /**
     * Setup smart auto-refresh: checks every 5 minutes if data is more than 10 minutes old
     * Uses refs for fetchAllData/cacheInfo/isStale to prevent unnecessary re-renders
     */
    const fetchAllDataRef = useRef(fetchAllData);
    const cacheInfoRef = useRef(cacheInfo);
    const isStaleRef = useRef(isStale);

    // Keep refs updated
    useEffect(() => { fetchAllDataRef.current = fetchAllData; }, [fetchAllData]);
    useEffect(() => { cacheInfoRef.current = cacheInfo; }, [cacheInfo]);
    useEffect(() => { isStaleRef.current = isStale; }, [isStale]);

    useEffect(() => {
        // Clear any existing timer first
        if (freshnessTimerRef.current) {
            clearInterval(freshnessTimerRef.current);
            freshnessTimerRef.current = null;
        }

        // Only set up the timer when not loading
        if (!dashboardData.loading) {
            freshnessTimerRef.current = setInterval(() => {
                const cache = cacheInfoRef.current;
                if (!cache || cache.age === null || cache.age === undefined) return;

                if (isStaleRef.current(cache.age)) {
                    fetchAllDataRef.current();
                }
            }, REFRESH_INTERVALS.FRESHNESS_CHECK);
        }

        // Cleanup function - always clear on unmount or dependency change
        return () => {
            if (freshnessTimerRef.current) {
                clearInterval(freshnessTimerRef.current);
                freshnessTimerRef.current = null;
            }
        };
    }, [dashboardData.loading]); // Reduced dependencies - only re-run when loading state changes

    // ========================================
    // AUTO-REFRESH WITH HOOKS
    // ========================================

    // Tier 0: Active users auto-refresh (60s)
    useAutoRefresh(
        refreshActiveUsersOnly,
        AUTO_REFRESH_INTERVALS.ACTIVE_USERS,
        autoRefreshEnabled,
        []
    );

    // Tier 0.5: Ping display auto-refresh (60s — UI only, no alert logic)
    useAutoRefresh(
        refreshPingDisplay,
        AUTO_REFRESH_INTERVALS.PING_DISPLAY,
        autoRefreshEnabled,
        []
    );

    // Tier 1: Full health check with alert reporting (15 min)
    useAutoRefresh(
        lightweightHealthCheck,
        AUTO_REFRESH_INTERVALS.HEALTH_CHECK,
        autoRefreshEnabled,
        []
    );

    // Budget monitoring with initial run
    useEffect(() => {
        if (!autoRefreshEnabled) return;

        monitorAndEnforceBudgets();

        const interval = setInterval(() => {
            monitorAndEnforceBudgets();
        }, AUTO_REFRESH_INTERVALS.BUDGET_MONITOR);

        return () => clearInterval(interval);
    }, [autoRefreshEnabled, monitorAndEnforceBudgets]);

    /**
     * Initial data load on component mount
     */
    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    // ========================================
    // SOCKET.IO REAL-TIME UPDATES
    // ========================================

    /**
     * Initialize socket connection and set up event listeners
     * for real-time data synchronization across concurrent users
     */
    useEffect(() => {
        // Connect to socket server with JWT for authentication.
        // Before each reconnect attempt, refresh the MSAL token so an expired
        // token does not cause the backend to permanently reject the socket.
        let socketInstance = null;
        getIdToken().then(token => {
            socketInstance = connectSocket(token);
            socketInstance.io.on('reconnect_attempt', () => {
                getIdToken()
                    .then(freshToken => setSocketToken(freshToken))
                    .catch(() => {});
            });
        }).catch(() => connectSocket(null));

        // Set up event listeners for real-time updates
        const unsubscribers = [];

        // Team blocked/unblocked - refresh teams data
        unsubscribers.push(
            onSocketEvent(SOCKET_EVENTS.TEAM_BLOCKED, () => {
                console.log('[Socket] Team blocked event received');
                fetchTeamsOnly();
            })
        );

        unsubscribers.push(
            onSocketEvent(SOCKET_EVENTS.TEAM_UNBLOCKED, () => {
                console.log('[Socket] Team unblocked event received');
                fetchTeamsOnly();
            })
        );

        // Budget updated - refresh teams data
        unsubscribers.push(
            onSocketEvent(SOCKET_EVENTS.BUDGET_UPDATED, () => {
                console.log('[Socket] Budget updated event received');
                fetchTeamsOnly();
            })
        );

        // Team updated - refresh teams data
        unsubscribers.push(
            onSocketEvent(SOCKET_EVENTS.TEAM_UPDATED, () => {
                console.log('[Socket] Team updated event received');
                fetchTeamsOnly();
            })
        );

        // User disabled/enabled - refresh to update UI state
        unsubscribers.push(
            onSocketEvent(SOCKET_EVENTS.USER_DISABLED, (data) => {
                console.log('[Socket] User disabled event received:', data);
                // Trigger a re-render to update user status displays
                setRenderKey(prev => prev + 1);
            })
        );

        unsubscribers.push(
            onSocketEvent(SOCKET_EVENTS.USER_ENABLED, (data) => {
                console.log('[Socket] User enabled event received:', data);
                // Trigger a re-render to update user status displays
                setRenderKey(prev => prev + 1);
            })
        );

        // Generic data change - full refresh
        unsubscribers.push(
            onSocketEvent(SOCKET_EVENTS.DATA_CHANGED, (data) => {
                console.log('[Socket] Data changed event received:', data);
                fetchTeamsOnly();
            })
        );

        // Model health update broadcast from backend after a fresh check
        unsubscribers.push(
            onSocketEvent('modelHealthUpdate', (cache) => {
                setDashboardData(prev => ({
                    ...prev,
                    health: {
                        healthy_endpoints: cache.healthy_endpoints || [],
                        unhealthy_endpoints: cache.unhealthy_endpoints || [],
                    },
                    modelHealthCheckedAt: cache.checkedAt || null,
                }));
            })
        );

        // Cleanup on unmount
        return () => {
            unsubscribers.forEach(unsub => unsub());
            disconnectSocket();
        };
    }, [fetchTeamsOnly]);


    /**
     * Handle manual refresh button click
     * Forces a fresh health check to bypass cache and filter out transient errors
     */
    const handleManualRefresh = () => {
        if (isRefreshing || isOnCooldown) return;
        startCooldown();
        fetchAllData(true); // Force health refresh to bypass cache
    };

    /**
     * Handle settings save
     */
    const handleSettingsSave = () => {
        // Settings saved
    };

    /**
     * Handle team click from AI chat sidebar
     * Opens TeamDetailsModal with the selected team
     */
    const handleChatTeamClick = useCallback((teamData) => {
        // Try to find the full team data from dashboard
        const fullTeam = dashboardData.teams?.find(t =>
            t.team_id === teamData.team_id ||
            t.team_alias === teamData.team_alias ||
            t.team_alias === teamData.name
        );
        setChatSelectedTeam(fullTeam || teamData);
    }, [dashboardData.teams]);

    /**
     * Handle user click from AI chat sidebar
     * Opens UserDetailsModal with the selected user
     */
    const handleChatUserClick = useCallback((userData) => {
        // Try to find the full user data from dashboard
        const fullUser = dashboardData.users?.find(u =>
            u.user_id === userData.user_id ||
            u.id === userData.id ||
            u.email === userData.user_email ||
            u.email === userData.email
        );
        setChatSelectedUser(fullUser || userData);
    }, [dashboardData.users]);

    /**
     * Handle settings open from AI chat sidebar
     * Opens Settings modal to the specified tab
     */
    const handleChatOpenSettings = useCallback((tab = 'thresholds') => {
        setSettingsSubTab(tab);
        setCurrentView('settings');
    }, []);

    /**
     * Handle navigation from AI chat sidebar
     * Scrolls to the specified section or opens the appropriate modal
     */
    const handleChatNavigate = useCallback((target) => {
        if (target === 'team_management') {
            // Scroll to Team Management section
            const teamSection = document.querySelector('.team-management-section, [data-section="team-management"]');
            if (teamSection) {
                teamSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                // Fallback: scroll to main content area
                window.scrollTo({ top: 400, behavior: 'smooth' });
            }
        } else if (target === 'audit_log') {
            setShowAuditLog(true);
        }
    }, []);

    // Show setup screen if API key is not configured
    if (!isConfigured) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'var(--bg)',
            }}>
                <div className="card" style={{ maxWidth: '700px', margin: '2rem' }}>
                    <h1 style={{ color: 'var(--fg-primary)', marginTop: 0 }}>Setup Required</h1>
                    <p>Welcome to <strong>Ask Operations</strong>!</p>
                    <p>To get started, please verify your environment setup:</p>
                    <ol style={{ lineHeight: '1.8' }}>
                        <li>Create or open your <code>.env</code> file in the project root</li>
                        <li>
                            Add your LiteLLM API key:
                            <pre style={{
                                background: 'var(--surface-alt)',
                                padding: '0.5rem',
                                borderRadius: '6px',
                                marginTop: '0.5rem',
                            }}>
                                {`REACT_APP_API_BASE_URL=${config.apiBaseUrl}\nREACT_APP_API_KEY=sk-xxxxxxxxxxxxxxxxx`}
                            </pre>
                        </li>
                        <li>Restart the app using <code>npm start</code> or <code>yarn start</code></li>
                    </ol>
                    <div className="message message-warning">
                        <strong>Note:</strong> Ensure the API key has admin permissions!
                    </div>
                </div>
            </div>
        );
    }

    // Get current user display name
    const userName = accounts[0]?.name || 'User';

    const language = i18n.language?.split('-')[0] || 'en';
    const handleToggleLanguage = () => {
        const newLang = language === 'en' ? 'th' : 'en';
        i18n.changeLanguage(newLang);
        localStorage.setItem('ask-operations-language', newLang);
    };

    const viewLabel = currentView === 'analytics' ? 'Analytics' : currentView === 'settings' ? 'Settings' : currentView === 'reports' ? 'Reports' : 'Dashboard / System Health & Teams';

    // Department sync callable by any component — acquires Graph token for the current user,
    // triggers sync, then refreshes departments and manager state
    const syncDepartmentsNow = async () => {
        const account = accounts[0];
        let graphToken;
        try {
            const tokenRes = await instance.acquireTokenSilent({
                scopes: ['https://graph.microsoft.com/User.Read.All'],
                account,
            });
            graphToken = tokenRes.accessToken;
        } catch {
            const tokenRes = await instance.acquireTokenPopup({
                scopes: ['https://graph.microsoft.com/User.Read.All'],
                account,
            });
            graphToken = tokenRes.accessToken;
        }
        const syncResult = await triggerDepartmentSync(graphToken);
        if (syncResult.success) {
            const [refreshed, team] = await Promise.all([getDepartments(), getMyTeam()]);
            if (refreshed.success) {
                setDashboardData(prev => ({ ...prev, departments: refreshed.data }));
                setRenderKey(prev => prev + 1);
            }
            setIsManager(team.isManager);
            setMyDirectReports(team.directReports);
        }
        return syncResult;
    };

    return (
        <>
            {/* Sidebar */}
            <Sidebar
                isOpen={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                view={currentView}
                onDashboard={() => { setCurrentView('dashboard'); }}
                onAnalytics={() => { setCurrentView('analytics'); }}
                onReports={() => { setReportInitialType(''); setCurrentView('reports'); }}
                onAuditLog={() => { setCurrentView('dashboard'); setShowAuditLog(true); }}
                onHelp={() => { setShowChatSidebar(true); }}
                onSettings={() => { setCurrentView('settings'); }}
                onLogout={handleLogout}
                userEmail={userEmail}
                userName={userName}
                userPhoto={userPhoto}
                userOrgUnit={userOrgUnit}
                userDepartment={userDepartment}
                isAdmin={isCurrentUserAdmin}
                isManager={isManager}
                myDirectReports={myDirectReports}
                language={language}
                onToggleLanguage={handleToggleLanguage}
                analyticsSubTab={analyticsSubTab}
                onAnalyticsSubTab={(tab) => { setAnalyticsSubTab(tab); setCurrentView('analytics'); }}
                settingsSubTab={settingsSubTab}
                onSettingsSubTab={(tab) => { setSettingsSubTab(tab); setCurrentView('settings'); }}
            />

            {/* Sticky header */}
            <header className="header">
                <div className="header-left">
                    <button
                        className="header-hamburger"
                        onClick={() => setSidebarOpen(true)}
                        aria-label="Open menu"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M4 6h16" /><path d="M4 12h16" /><path d="M4 18h16" />
                        </svg>
                    </button>
                    <button
                        className="header-app-name-btn"
                        onClick={() => setCurrentView('dashboard')}
                        aria-label="Go to dashboard"
                    >
                        <div className="header-app-name">Ask Operations</div>
                    </button>
                </div>

                <div className="header-right">
                    {cacheInfo && !isRefreshing && (
                        <div className="cache-pill">
                            <span className={`cache-dot ${freshnessStatus.status === 'stale' ? 'stale' : 'fresh'}`} />
                            <span className="cache-label">
                                {cacheInfo.age > 0 ? formatCacheAge(cacheInfo.age) : 'Live'}
                            </span>
                        </div>
                    )}
                    {/* Theme toggle */}
                    <button
                        className="header-refresh-btn"
                        onClick={toggleTheme}
                        aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
                        title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
                    >
                        {theme === 'dark' ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="5" />
                                <line x1="12" y1="1" x2="12" y2="3" />
                                <line x1="12" y1="21" x2="12" y2="23" />
                                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                                <line x1="1" y1="12" x2="3" y2="12" />
                                <line x1="21" y1="12" x2="23" y2="12" />
                                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                            </svg>
                        ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                            </svg>
                        )}
                    </button>
                    {/* Notifications bell */}
                    <div style={{ position: 'relative' }}>
                        <button
                            className="header-refresh-btn"
                            onClick={() => { setNotificationsOpen(v => !v); setHasUnread(false); }}
                            aria-label="Notifications"
                            title="Notifications"
                            style={{ position: 'relative' }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                            </svg>
                            {hasUnread && <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--critical)', position: 'absolute', top: 4, right: 4, border: '1px solid var(--surface)' }} />}
                        </button>
                        {notificationsOpen && (
                            <>
                                <div style={{ position: 'fixed', inset: 0, zIndex: 98 }} onClick={() => setNotificationsOpen(false)} />
                                <div style={{
                                    position: 'absolute', top: '100%', right: 0, zIndex: 99,
                                    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
                                    boxShadow: 'var(--shadow-panel)', marginTop: 6,
                                    width: 320, maxHeight: 360, overflowY: 'auto',
                                }}>
                                    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: '0.82rem', fontWeight: 700, color: 'var(--fg-primary)' }}>Alerts</div>
                                    {notifications.length === 0 ? (
                                        <div style={{ padding: '16px', color: 'var(--muted)', fontSize: '0.82rem', textAlign: 'center' }}>No recent alerts</div>
                                    ) : notifications.map((n, i) => {
                                        const actionLabels = {
                                            team_blocked: 'Team blocked',
                                            team_unblocked: 'Team unblocked',
                                            user_blocked: 'User blocked',
                                            team_budget_updated: 'Budget updated',
                                        };
                                        const ts = n.timestamp ? new Date(n.timestamp).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
                                        const dotColor = (n.action === 'team_blocked' || n.action === 'user_blocked') ? 'var(--critical)' : 'var(--warning)';
                                        return (
                                            <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border)', alignItems: 'flex-start' }}>
                                                <div style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor, flexShrink: 0, marginTop: 5 }} />
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontSize: '0.8rem', color: 'var(--fg-primary)', fontWeight: 600 }}>{actionLabels[n.action] || n.action}</div>
                                                    {n.details && <div style={{ fontSize: '0.72rem', color: 'var(--fg-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.details}</div>}
                                                    {ts && <div style={{ fontSize: '0.68rem', color: 'var(--muted)', marginTop: 2 }}>{ts}</div>}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                    <a
                        href="https://ask.aware.co.th/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="header-refresh-btn header-refresh-btn--accent"
                        style={{ textDecoration: 'none' }}
                        title="Open Ask Aware"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                            <polyline points="15 3 21 3 21 9" />
                            <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                        <span className="header-btn-label">Ask Aware</span>
                    </a>
                    <button
                        className="header-refresh-btn"
                        onClick={handleManualRefresh}
                        disabled={isRefreshing || isOnCooldown}
                        aria-label="Refresh now"
                        title={isOnCooldown ? `Wait ${remainingSeconds}s` : t('common.refresh')}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={isRefreshing ? 'spinning' : ''}>
                            <path d="M23 4v6h-6" /><path d="M1 20v-6h6" />
                            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" /><path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
                        </svg>
                        <span className="header-btn-label">Refresh</span>
                    </button>
                </div>
            </header>

            {/* Breadcrumb — desktop only, hidden on dashboard and full-page views that have their own header */}
            {currentView !== 'dashboard' && currentView !== 'analytics' && currentView !== 'reports' && (
                <div className="breadcrumb">
                    <button
                        className="breadcrumb-l1 breadcrumb-link"
                        onClick={() => setCurrentView('dashboard')}
                    >
                        {t('nav.dashboard')}
                    </button>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m9 18 6-6-6-6" />
                    </svg>
                    <span className="breadcrumb-l2">{viewLabel}</span>
                </div>
            )}

            {/* Main content — dashboard, analytics, settings, or reports inline */}
            {currentView === 'analytics' ? (
            <div style={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <UsageCostDashboard
                    isAdmin={isCurrentUserAdmin}
                    teams={dashboardData.teams || []}
                    groups={dashboardData.groups || []}
                    users={dashboardData.users || []}
                    allModels={dashboardData.models || []}
                    onClose={() => setCurrentView('dashboard')}
                    userEmail={userEmail}
                    onRefresh={fetchTeamsOnly}
                    adminUsersData={dashboardData.adminUsers}
                    analyticsSubTab={analyticsSubTab}
                    onAnalyticsSubTabChange={setAnalyticsSubTab}
                    onSwitchToReports={(rt) => { setReportInitialType(rt); setCurrentView('reports'); }}
                    initialReportType={reportInitialType}
                    isManager={isManager}
                    myDirectReports={myDirectReports}
                    onSyncDepartments={syncDepartmentsNow}
                />
            </div>
            ) : currentView === 'reports' ? (
            <div style={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <UsageCostDashboard
                    isAdmin={isCurrentUserAdmin}
                    teams={dashboardData.teams || []}
                    groups={dashboardData.groups || []}
                    users={dashboardData.users || []}
                    allModels={dashboardData.models || []}
                    onClose={() => setCurrentView('dashboard')}
                    userEmail={userEmail}
                    onRefresh={fetchTeamsOnly}
                    adminUsersData={dashboardData.adminUsers}
                    analyticsSubTab="reports"
                    onAnalyticsSubTabChange={() => {}}
                    initialReportType={reportInitialType}
                    isManager={isManager}
                    myDirectReports={myDirectReports}
                    onSyncDepartments={syncDepartmentsNow}
                />
            </div>
            ) : currentView === 'settings' ? (
            <div style={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Settings
                    isOpen={true}
                    onClose={() => setCurrentView('dashboard')}
                    onSave={handleSettingsSave}
                    allUsers={dashboardData.users || []}
                    initialTab={settingsSubTab}
                    onTabChange={setSettingsSubTab}
                />
            </div>
            ) : (
                <main className="main-content">
                    {dashboardData.error && (
                        <div className="card">
                            <div className="alert-banner">
                                <div>
                                    <div className="alert-banner-title">Error Loading Data</div>
                                    <div className="alert-banner-body">{dashboardData.error}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    <SystemHealth
                        key={`health-${renderKey}`}
                        data={dashboardData}
                        loading={dashboardData.loading}
                        loadingStatus={loadingStatus}
                        loadingMessage={healthLoadingMessage}
                        onProvision={() => setShowProvisionModal(true)}
                        mcpServers={dashboardData.mcpServers || []}
                        owuiTools={dashboardData.owuiTools || []}
                        owuiSkills={dashboardData.owuiSkills || []}
                        isAdmin={isCurrentUserAdmin}
                        onCheckModels={() => triggerModelHealthCheck().then(result => {
                            if (result.success && result.cache) {
                                setDashboardData(prev => ({
                                    ...prev,
                                    health: {
                                        healthy_endpoints: result.cache.healthy_endpoints || [],
                                        unhealthy_endpoints: result.cache.unhealthy_endpoints || [],
                                    },
                                    modelHealthCheckedAt: result.cache.checkedAt || null,
                                }));
                            }
                        })}
                    />
                    <TeamManagement
                        data={dashboardData}
                        loading={dashboardData.loading}
                        loadingStatus={loadingStatus}
                        loadingMessage={teamLoadingMessage}
                        onRefresh={fetchTeamsOnly}
                    />
                </main>
            )}

            {/* Mobile bottom nav spacer + bar */}
            <div className="bottom-nav-spacer" />
            <nav className="bottom-nav">
                <button
                    className={`bottom-nav-btn ${currentView === 'dashboard' ? 'active' : ''}`}
                    onClick={() => setCurrentView('dashboard')}
                    aria-label="Dashboard"
                >
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" />
                        <rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" />
                    </svg>
                    Dashboard
                </button>
                <button
                    className={`bottom-nav-btn ${currentView === 'analytics' ? 'active' : ''}`}
                    onClick={() => { setCurrentView('analytics'); setAnalyticsSubTab('my-usage'); }}
                    aria-label="Analytics"
                >
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                    </svg>
                    Analytics
                </button>
                <button
                    className={`bottom-nav-btn ${currentView === 'settings' ? 'active' : ''}`}
                    onClick={() => setSidebarOpen(true)}
                    aria-label="Menu"
                >
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M4 6h16" /><path d="M4 12h16" /><path d="M4 18h16" />
                    </svg>
                    Menu
                </button>
            </nav>

            {/* Modal components */}
            {showProvisionModal && (
                <ProvisionModal
                    onClose={() => setShowProvisionModal(false)}
                    currentUserEmail={userEmail}
                    isAdmin={isCurrentUserAdmin}
                />
            )}
            {showAuditLog && (
                <AuditLog
                    onClose={() => setShowAuditLog(false)}
                    currentUserEmail={userEmail}
                    teams={dashboardData.teams}
                />
            )}

            {/* AI Chat Sidebar (Help) */}
            <ChatSidebar
                isOpen={showChatSidebar}
                onClose={() => setShowChatSidebar(false)}
                onOpenTeam={handleChatTeamClick}
                onOpenUser={handleChatUserClick}
                onOpenSettings={handleChatOpenSettings}
                onNavigate={handleChatNavigate}
                onOpenHelp={() => {}}
                visibleTeamIds={(dashboardData.teams || [])
                    .map(t => t.team_id)
                    .filter(Boolean)
                }
            />

            {/* Chat-triggered Team Details Modal */}
            {chatSelectedTeam && (
                <TeamDetailsModal
                    team={chatSelectedTeam}
                    allUsers={dashboardData.users || []}
                    allGroups={dashboardData.groups || []}
                    onClose={() => setChatSelectedTeam(null)}
                    onUpdate={fetchTeamsOnly}
                />
            )}

            {/* Chat-triggered User Details Modal */}
            {chatSelectedUser && (
                <UserDetailsModal
                    user={chatSelectedUser}
                    onClose={() => setChatSelectedUser(null)}
                />
            )}


            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
            `}</style>
        </>
    );
}

export default App;
